import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import Input from '../components/Input';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';

import LogoImage from '../assets/1.png';

export default function Login() {
  const { t } = useI18n();
  const { login } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = t.auth.errors.requiredField;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t.auth.errors.invalidEmail;
    }

    if (!formData.password) {
      newErrors.password = t.auth.errors.requiredField;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await login(formData);
      
      // The workspace API restores onboarding or the student's existing application.
      navigate('/app');
    } catch (error: any) {
      console.error('Login error:', error);

      if (error.response?.status === 401) {
        setErrors({ form: t.auth.errors.invalidCredentials });
        // Clear email and password fields on invalid credentials
        setFormData({ email: '', password: '' });
      } else if (error.response?.status === 403) {
        // Email not verified - redirect to verification page
        navigate('/verify-email', { state: { email: formData.email } });
      } else {
        setErrors({ form: t.auth.errors.networkError });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F4EEFA] via-white to-[#E8F8F5] relative overflow-hidden py-12 px-4 sm:px-6 lg:px-8">
      {/* Animated Background Shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Grid Background */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundSize: '40px 40px',
            backgroundImage: 'linear-gradient(to right, #e4e4e7 1px, transparent 1px), linear-gradient(to bottom, #e4e4e7 1px, transparent 1px)',
          }}
        />

        {/* Floating Shapes */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-gradient-to-br from-[#B18AE0] to-[#5B3FD0] rounded-full opacity-20"></div>
        <div className="absolute top-40 right-20 w-16 h-16 bg-[#9FFFCB] opacity-20 rounded-xl rotate-45"></div>
        <div className="absolute bottom-32 left-20 w-14 h-14 bg-[#5B3FD0] rounded-full opacity-15"></div>
        <div className="absolute bottom-20 right-32 w-12 h-12 bg-gradient-to-br from-[#B18AE0] to-[#9FFFCB] opacity-20 rounded-lg rotate-12"></div>
      </div>

      <div className="max-w-md w-full space-y-8 relative z-10">
        {/* Logo/Brand */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center justify-center gap-3 group mb-8">
            <img src={LogoImage} alt="EduNasser Logo" className="w-32 h-32 object-contain" />
          </Link>

          <h2 className="text-4xl font-bold">
            <span className="text-[#2C2C2C]">{t.auth.login.title}</span>
          </h2>
          <p className="mt-2 text-gray-600">
            {t.auth.login.subtitle}
          </p>
        </div>

        {/* Login Form Card */}
        <form
          className="mt-8 bg-white/80 backdrop-blur-xl p-8 rounded-3xl shadow-2xl border border-white/50 space-y-6"
          onSubmit={handleSubmit}
        >
          {/* Decorative top border */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#B18AE0] via-[#5B3FD0] to-[#9FFFCB] rounded-t-3xl"></div>

          {errors.form && (
            <div className="bg-red-50/80 backdrop-blur-sm border border-red-400 text-red-700 px-4 py-3 rounded-xl">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {errors.form}
              </div>
            </div>
          )}

          <Input
            label={t.auth.login.email}
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
            required
            autoComplete="email"
            disabled={loading}
          />

          <Input
            label={t.auth.login.password}
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            error={errors.password}
            required
            autoComplete="current-password"
            disabled={loading}
          />

          <div className="flex items-center justify-between">
            <div className="text-sm">
              <Link
                to="/forgot-password"
                className="text-[#5B3FD0] hover:text-[#B18AE0] font-medium"
              >
                {t.auth.login.forgotPassword}
              </Link>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full shadow-lg hover:shadow-xl"
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <LoadingSpinner size="sm" />
                <span>{t.common.loading}</span>
              </div>
            ) : (
              <>
                {t.auth.login.button}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </>
            )}
          </Button>

          <div className="text-center pt-4">
            <p className="text-sm text-gray-600">
              {t.auth.login.noAccount}{' '}
              <Link
                to="/register"
                className="text-[#5B3FD0] hover:text-[#B18AE0] font-bold"
              >
                {t.auth.login.signupLink}
              </Link>
            </p>
          </div>
        </form>

        {/* Back to Home Link */}
        <div className="text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#707070] hover:text-[#5B3FD0] font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
